import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import { EnumContainerTipo } from '../../../../../../../shared/enums';
import { ContainerService } from '../../../../../../../shared/services/api/c4-model/ContainerService';

type TipoContainer = {
  id: number;
  nome: string;
};

type AcessoContainerPayload = {
  container: EnumContainerTipo;
  acao: string;
  meioAcesso: string;
};

type Row = {
  id: string;
  container: EnumContainerTipo;
  acao: string;
  meioAcesso: string;
  isEditing?: boolean;
  isNew?: boolean;
};

type Props = {
  initialRows?: AcessoContainerPayload[];
  onContainersChange?: (containers: AcessoContainerPayload[]) => void;
};

const convertToRows = (containers: AcessoContainerPayload[]): Row[] => {
  return (
    containers?.map((item, index) => ({
      id: `container-${index}`,
      container: item.container,
      acao: item.acao,
      meioAcesso: item.meioAcesso,
    })) || []
  );
};

const convertToContainers = (rows: Row[]): AcessoContainerPayload[] => {
  return (rows || [])
    .filter((row) => !row.isNew)
    .map((row) => ({
      container: row.container,
      acao: row.acao,
      meioAcesso: row.meioAcesso,
    }));
};

export const ListaContainerSistema: React.FC<Props> = ({
  initialRows = [],
  onContainersChange,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [editingValues, setEditingValues] = useState<{
    [key: string]: {
      container: EnumContainerTipo;
      acao: string;
      meioAcesso: string;
    };
  }>({});
  const [tiposContainer, setTiposContainer] = useState<TipoContainer[]>([]);

  // Function to get container type name by id
  const getTipoContainerNome = (id: number): string => {
    const tipo = tiposContainer.find((t) => t.id === id);
    return tipo ? tipo.nome : `Tipo ${id}`;
  };

  // Load container types
  useEffect(() => {
    const loadTiposContainer = async () => {
      try {
        const result = await ContainerService.getAll();
        if (result && !('message' in result)) {
          setTiposContainer(result);
        }
      } catch (error) {
        console.error('Erro ao carregar tipos de container:', error);
        // Set fallback values if service fails
        setTiposContainer([
          { id: 0, nome: 'PARCEL' },
          { id: 1, nome: 'FRONTEND' },
          { id: 2, nome: 'BACKEND' },
          { id: 3, nome: 'BIBLIOTECA' },
          { id: 4, nome: 'FULLSTACK' },
          { id: 5, nome: 'WEBCOMPONENT' },
          { id: 6, nome: 'ORQUESTRADOR' },
        ]);
      }
    };
    loadTiposContainer();
  }, []);

  useEffect(() => {
    if (initialRows && initialRows.length > 0) {
      setRows(convertToRows(initialRows));
    } else {
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;

    setRows(rows?.map((r) => (r.id === id ? { ...r, isEditing: true } : r)));
    setEditingValues({
      ...editingValues,
      [id]: {
        container: row.container,
        acao: row.acao,
        meioAcesso: row.meioAcesso,
      },
    });
  };

  const handleCancelEdit = (id: string) => {
    setRows(
      rows
        ?.map((r) => {
          if (r.id === id) {
            if (r.isNew) {
              return null;
            }
            return { ...r, isEditing: false };
          }
          return r;
        })
        .filter(Boolean) as Row[]
    );

    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);
  };

  const handleSaveEdit = (id: string) => {
    const editingValue = editingValues[id];
    if (
      !editingValue ||
      !editingValue.acao?.trim() ||
      !editingValue.meioAcesso?.trim()
    ) {
      return;
    }

    const newRows = rows?.map((r) =>
      r.id === id
        ? {
          ...r,
          container: editingValue.container,
          acao: editingValue.acao.trim(),
          meioAcesso: editingValue.meioAcesso.trim(),
          isEditing: false,
          isNew: false,
        }
        : r
    );

    setRows(newRows);

    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);

    if (onContainersChange) {
      onContainersChange(convertToContainers(newRows));
    }
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);

    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);

    if (onContainersChange) {
      onContainersChange(convertToContainers(newRows));
    }
  };

  const handleAddNew = () => {
    const newRow: Row = {
      id: `temp-new-${Date.now()}`,
      container: 0, // PARCEL
      acao: '',
      meioAcesso: '',
      isEditing: true,
      isNew: true,
    };

    setRows([...rows, newRow]);
    setEditingValues({
      ...editingValues,
      [newRow.id]: {
        container: 0, // PARCEL
        acao: '',
        meioAcesso: '',
      },
    });
  };

  const handleEditingValueChange = (id: string, field: string, value: any) => {
    setEditingValues({
      ...editingValues,
      [id]: {
        ...editingValues[id],
        [field]: value,
      },
    });
  };

  const canSave = (id: string) => {
    const editingValue = editingValues[id];
    return (
      editingValue &&
      editingValue.acao?.trim() &&
      editingValue.meioAcesso?.trim()
    );
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={2}>
      <Button
        variant="contained"
        color="primary"
        size="small"
        sx={{ width: '80px' }}
        onClick={handleAddNew}
        startIcon={<AddIcon />}
      >
        Novo
      </Button>

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Container</TableCell>
              <TableCell>Ação</TableCell>
              <TableCell>Meio de Acesso</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  {row.isEditing ? (
                    <>
                      <Tooltip title="Salvar">
                        <IconButton
                          size="small"
                          onClick={() => handleSaveEdit(row.id)}
                          color="primary"
                          disabled={!canSave(row.id)}
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancelar">
                        <IconButton
                          size="small"
                          onClick={() => handleCancelEdit(row.id)}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(row.id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(row.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  {row.isEditing ? (
                    <FormControl size="small" fullWidth>
                      <Select
                        value={editingValues[row.id]?.container || 0}
                        onChange={(e) =>
                          handleEditingValueChange(
                            row.id,
                            'container',
                            e.target.value
                          )
                        }
                        variant="outlined"
                      >
                        {tiposContainer?.map((tipo) => (
                          <MenuItem key={tipo.id} value={tipo.id}>
                            {tipo.nome}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    getTipoContainerNome(row.container)
                  )}
                </TableCell>
                <TableCell>
                  {row.isEditing ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editingValues[row.id]?.acao || ''}
                      onChange={(e) =>
                        handleEditingValueChange(row.id, 'acao', e.target.value)
                      }
                      variant="outlined"
                      placeholder="Ex: READ"
                    />
                  ) : (
                    row.acao
                  )}
                </TableCell>
                <TableCell>
                  {row.isEditing ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editingValues[row.id]?.meioAcesso || ''}
                      onChange={(e) =>
                        handleEditingValueChange(
                          row.id,
                          'meioAcesso',
                          e.target.value
                        )
                      }
                      variant="outlined"
                      placeholder="Ex: REST API"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (canSave(row.id)) {
                            handleSaveEdit(row.id);
                          }
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelEdit(row.id);
                        }
                      }}
                    />
                  ) : (
                    row.meioAcesso
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
